import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { TableFieldType } from "../table-field/table-field.model";

export type ChartType = "BAR" | "LINE" | "PIE" | "SURFACE_3D" | "AREA_BANDED_2D";
export type ChartAggregation =
  | "NONE"
  | "SUM"
  | "AVG"
  | "COUNT"
  | "MIN"
  | "MAX";

export interface ChartTypeMeta {
  id: ChartType;
  label: string;
  sub: string;
  needsYAxis: boolean;
  needsBands: boolean;
  maxSeries: number;
}

/**
 * Single source of truth for chart-type driven behaviour. All conditional field
 * visibility, dropdown filtering and series-count limits derive from this table
 * (mirrors CHART_TYPES in the chart_design_studio.html prototype).
 */
export const CHART_TYPE_META: Record<ChartType, ChartTypeMeta> = {
  BAR: {
    id: "BAR",
    label: $localize`Bar`,
    sub: $localize`category → values`,
    needsYAxis: false,
    needsBands: false,
    maxSeries: 6,
  },
  LINE: {
    id: "LINE",
    label: $localize`Line`,
    sub: $localize`category → values`,
    needsYAxis: false,
    needsBands: false,
    maxSeries: 6,
  },
  PIE: {
    id: "PIE",
    label: $localize`Pie`,
    sub: $localize`category → single value`,
    needsYAxis: false,
    needsBands: false,
    maxSeries: 1,
  },
  SURFACE_3D: {
    id: "SURFACE_3D",
    label: $localize`3D surface (banded)`,
    sub: $localize`X, Y → banded Z`,
    needsYAxis: true,
    needsBands: true,
    maxSeries: 1,
  },
  AREA_BANDED_2D: {
    id: "AREA_BANDED_2D",
    label: $localize`Banded area (2D)`,
    sub: $localize`X, Y → banded Z`,
    needsYAxis: true,
    needsBands: true,
    maxSeries: 1,
  },
};

export const CHART_TYPES: ChartTypeMeta[] = Object.values(CHART_TYPE_META);

export const CHART_AGGREGATIONS: ChartAggregation[] = [
  "NONE",
  "SUM",
  "AVG",
  "COUNT",
  "MIN",
  "MAX",
];

export const DEFAULT_SERIES_COLORS = [
  "#2F5FDB",
  "#C0504D",
  "#9BBB59",
  "#8064A2",
  "#E0A030",
  "#3FA7A2",
];

// low → high: blue, green, yellow, red — a traffic-light style scale
export const DEFAULT_BAND_EDGES = [0, 0.25, 0.5, 0.75, 1.0];
export const DEFAULT_BAND_COLORS = ["#4472C4", "#55A868", "#E8B33E", "#C0504D"];

const NUMERIC_FIELD_TYPES: TableFieldType[] = [
  TableFieldType.INTEGER,
  TableFieldType.DECIMAL,
];

export function isNumericFieldType(type: TableFieldType | number): boolean {
  return NUMERIC_FIELD_TYPES.includes(+type as TableFieldType);
}

/** Minimal column shape the chart designer needs to reason about a column. */
export interface ColumnLike {
  columnKey: string;
  name?: string;
  type: TableFieldType | number;
  values?: string[];
  /** JSON string (as stored on form_table_column) or an already-parsed map */
  valueMap?: string | Record<string, number> | null;
}

/**
 * A dropdown column can stand in for a numeric axis when every one of its
 * options has a numeric value assigned (the `valueMap`). Returns that
 * option → number map, or null when the column isn't a fully-mapped dropdown.
 */
export function dropdownNumericMap(
  col: ColumnLike,
): Record<string, number> | null {
  if (+col.type !== TableFieldType.DROPDOWN) {
    return null;
  }
  let raw: Record<string, unknown> | null = null;
  if (typeof col.valueMap === "string") {
    try {
      raw = JSON.parse(col.valueMap);
    } catch {
      return null;
    }
  } else if (col.valueMap && typeof col.valueMap === "object") {
    raw = col.valueMap as Record<string, unknown>;
  }
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const options =
    col.values && col.values.length > 0 ? col.values : Object.keys(raw);
  if (options.length === 0) {
    return null;
  }
  const map: Record<string, number> = {};
  for (const opt of options) {
    const n = Number(raw[opt]);
    if (!Number.isFinite(n)) {
      return null;
    }
    map[opt] = n;
  }
  return map;
}

/**
 * True when a column can fill a numeric role — a Y-axis, or the X/Y axis of a
 * banded (SURFACE_3D / AREA_BANDED_2D) chart. Numeric columns qualify directly;
 * dropdowns qualify when fully mapped to numeric values.
 */
export function isNumericAxisColumn(col: ColumnLike): boolean {
  return isNumericFieldType(col.type) || dropdownNumericMap(col) != null;
}

/** Predicate for the category / X-axis role, given the selected chart type. */
export function isCategoryColumnAllowed(
  meta: ChartTypeMeta,
  col: ColumnLike,
): boolean {
  if (meta.needsYAxis) {
    return isNumericAxisColumn(col);
  }
  return [
    TableFieldType.TEXT,
    TableFieldType.DROPDOWN,
    TableFieldType.INTEGER,
  ].includes(+col.type as TableFieldType);
}

/** Column types allowed for the category / X-axis role of the given chart type. */
export function categoryAllowedFieldTypes(
  meta: ChartTypeMeta,
): TableFieldType[] {
  return meta.needsYAxis
    ? [TableFieldType.INTEGER, TableFieldType.DECIMAL]
    : [TableFieldType.TEXT, TableFieldType.DROPDOWN, TableFieldType.INTEGER];
}

export function chartTypeMeta(type: ChartType): ChartTypeMeta {
  return CHART_TYPE_META[type] ?? CHART_TYPE_META.BAR;
}

export class ServiceChartSeries {
  valueColumnKey: string;
  label?: string;
  color?: string;
  sort: number;

  constructor(data?: Partial<ServiceChartSeries>) {
    if (data) {
      Object.assign(this, data);
    }
    this.sort = this.sort ?? 0;
  }

  toMap(series: ServiceChartSeries): Map<string, unknown> {
    const map = new Map<string, unknown>();
    map.set("valueColumnKey", series.valueColumnKey);
    if (series.label != null && series.label !== "") {
      map.set("label", series.label);
    }
    if (series.color != null && series.color !== "") {
      map.set("color", series.color);
    }
    map.set("sort", series.sort ?? 0);
    return map;
  }
}

export class ServiceChart {
  name: string;
  title?: string;
  renderTag: string;
  /** renderTag of the owning table — forms-ng reads the chart's rows by this key */
  tableKey?: string;
  chartType: ChartType;
  categoryColumnKey: string;
  yAxisColumnKey?: string;
  aggregation: ChartAggregation;
  xAxisLabel?: string;
  yAxisLabel?: string;
  zAxisLabel?: string;
  showLegend: boolean;
  showDataLabels: boolean;
  /** backend flag — also render the source data table beneath the chart */
  attachDataTable: boolean;
  widthPx: number;
  heightPx: number;
  bandEdges?: number[];
  bandColors?: string[];
  /** SURFACE_3D — camera yaw in degrees (Matplotlib ax.view_init azim) */
  viewAzimuth?: number;
  /** SURFACE_3D — camera pitch in degrees (Matplotlib ax.view_init elev) */
  viewElevation?: number;
  /** banded charts — surface / cell fill opacity 0.15–1 (Matplotlib alpha) */
  surfaceOpacity?: number;
  /** banded charts — spline-smooth the surface (backend: scipy interpolation) */
  smoothSurface?: boolean;
  sort: number;
  series: ServiceChartSeries[];

  constructor(data?: Partial<ServiceChart>) {
    if (data) {
      Object.assign(this, data);
    }
    this.chartType = this.chartType ?? "BAR";
    this.aggregation = this.aggregation ?? "NONE";
    this.showLegend = this.showLegend ?? true;
    this.showDataLabels = this.showDataLabels ?? false;
    this.attachDataTable = this.attachDataTable ?? false;
    this.widthPx = this.widthPx ?? 600;
    this.heightPx = this.heightPx ?? 350;
    this.sort = this.sort ?? 0;
    this.series = (this.series ?? []).map((s) => new ServiceChartSeries(s));
  }

  toMap(chart: ServiceChart): Map<string, unknown> {
    const meta = chartTypeMeta(chart.chartType);
    const map = new Map<string, unknown>();
    map.set("name", chart.name);
    map.set("renderTag", chart.renderTag);
    map.set("tableKey", chart.tableKey);
    map.set("chartType", chart.chartType);
    map.set("categoryColumnKey", chart.categoryColumnKey);
    if (meta.needsYAxis && chart.yAxisColumnKey != null) {
      map.set("yAxisColumnKey", chart.yAxisColumnKey);
    }
    map.set("aggregation", meta.needsBands ? "NONE" : chart.aggregation ?? "NONE");
    if (chart.title != null && chart.title !== "") {
      map.set("title", chart.title);
    }
    if (chart.xAxisLabel != null && chart.xAxisLabel !== "") {
      map.set("xAxisLabel", chart.xAxisLabel);
    }
    if (chart.yAxisLabel != null && chart.yAxisLabel !== "") {
      map.set("yAxisLabel", chart.yAxisLabel);
    }
    if (meta.needsYAxis && chart.zAxisLabel != null && chart.zAxisLabel !== "") {
      map.set("zAxisLabel", chart.zAxisLabel);
    }
    map.set("showLegend", chart.showLegend ?? true);
    map.set("showDataLabels", chart.showDataLabels ?? false);
    map.set("attachDataTable", chart.attachDataTable ?? false);
    map.set("widthPx", chart.widthPx ?? 600);
    map.set("heightPx", chart.heightPx ?? 350);
    if (meta.needsBands && chart.bandEdges != null) {
      map.set("bandEdges", [...chart.bandEdges]);
    }
    if (meta.needsBands && chart.bandColors != null) {
      map.set("bandColors", [...chart.bandColors]);
    }
    if (chart.chartType === "SURFACE_3D") {
      map.set("viewAzimuth", chart.viewAzimuth ?? 45);
      map.set("viewElevation", chart.viewElevation ?? 30);
    }
    if (meta.needsBands) {
      map.set("surfaceOpacity", chart.surfaceOpacity ?? 1);
      map.set("smoothSurface", chart.smoothSurface ?? true);
    }
    map.set("sort", chart.sort ?? 0);
    if (chart.series?.length > 0) {
      map.set(
        "series",
        chart.series.map((s, i) => {
          const ser = new ServiceChartSeries({
            valueColumnKey: s.valueColumnKey,
            label: s.label,
            color: meta.needsBands ? null : s.color,
            sort: s.sort ?? i,
          });
          return ser.toMap(ser);
        }),
      );
    }
    return map;
  }
}

export class ServiceChartSeriesForm {
  valueColumnKey = new FormControl();
  label = new FormControl();
  color = new FormControl();
  sort = new FormControl();

  constructor(series?: Partial<ServiceChartSeries>) {
    this.valueColumnKey.setValue(series?.valueColumnKey ?? null);
    this.label.setValue(series?.label ?? null);
    this.color.setValue(series?.color ?? DEFAULT_SERIES_COLORS[0]);
    this.sort.setValue(series?.sort ?? 0);
    this.valueColumnKey.setValidators([Validators.required]);
    this.valueColumnKey.updateValueAndValidity();
  }
}

export class ServiceChartForm {
  name = new FormControl();
  title = new FormControl();
  renderTag = new FormControl();
  chartType = new FormControl();
  categoryColumnKey = new FormControl();
  yAxisColumnKey = new FormControl();
  aggregation = new FormControl();
  xAxisLabel = new FormControl();
  yAxisLabel = new FormControl();
  zAxisLabel = new FormControl();
  showLegend = new FormControl();
  showDataLabels = new FormControl();
  attachDataTable = new FormControl();
  widthPx = new FormControl();
  heightPx = new FormControl();
  bandEdges = new FormArray([]);
  bandColors = new FormArray([]);
  viewAzimuth = new FormControl();
  viewElevation = new FormControl();
  surfaceOpacity = new FormControl();
  smoothSurface = new FormControl();
  sort = new FormControl();
  series = new FormArray([]);

  constructor(chart?: Partial<ServiceChart>) {
    const c = new ServiceChart(chart ?? {});
    this.name.setValue(c.name ?? null);
    this.title.setValue(c.title ?? null);
    this.renderTag.setValue(c.renderTag ?? null);
    this.chartType.setValue(c.chartType);
    this.categoryColumnKey.setValue(c.categoryColumnKey ?? null);
    this.yAxisColumnKey.setValue(c.yAxisColumnKey ?? null);
    this.aggregation.setValue(c.aggregation);
    this.xAxisLabel.setValue(c.xAxisLabel ?? null);
    this.yAxisLabel.setValue(c.yAxisLabel ?? null);
    this.zAxisLabel.setValue(c.zAxisLabel ?? null);
    this.showLegend.setValue(c.showLegend);
    this.showDataLabels.setValue(c.showDataLabels);
    this.attachDataTable.setValue(c.attachDataTable);
    this.widthPx.setValue(c.widthPx);
    this.heightPx.setValue(c.heightPx);
    this.viewAzimuth.setValue(c.viewAzimuth ?? null);
    this.viewElevation.setValue(c.viewElevation ?? null);
    this.surfaceOpacity.setValue(c.surfaceOpacity ?? null);
    this.smoothSurface.setValue(c.smoothSurface ?? null);
    this.sort.setValue(c.sort ?? 0);

    (c.bandEdges ?? []).forEach((e) =>
      this.bandEdges.push(new FormControl(e)),
    );
    (c.bandColors ?? []).forEach((col) =>
      this.bandColors.push(new FormControl(col)),
    );
    (c.series ?? []).forEach((s) =>
      this.series.push(new FormGroup(new ServiceChartSeriesForm(s) as unknown)),
    );

    this.name.setValidators([Validators.required]);
    this.renderTag.setValidators([Validators.required]);
    this.chartType.setValidators([Validators.required]);
    this.categoryColumnKey.setValidators([Validators.required]);
    this.name.updateValueAndValidity();
    this.renderTag.updateValueAndValidity();
    this.categoryColumnKey.updateValueAndValidity();
  }
}
